import { Card, CardContent } from "@libs/zapi-ui/shadcn";
import { useValidations } from "@/zapi-frontend/shared/hooks/useValidations";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { getAnswerStyles, baseButtonClasses, baseExplanationClasses } from "../lib/validation-styles";
import { ValidationStats } from "./_ValidationStats";
import { AppButton, LoadingSpinner, Typography } from "@libs/zapi-ui/custom";
import { CheckIcon, ProhibitIcon, XIcon } from "@phosphor-icons/react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";

type TValidationPanelProps = { projectId: string; isChapterContentReady: boolean };

export function ValidationPanel({ projectId, isChapterContentReady }: TValidationPanelProps) {
  const { activeChapter } = useChapterStore();
  const {
    questions,
    submitQuestion,
    validateAll,
    isCurrent,
    isFetchingQuestions,
    currentlyValidating,
    updateCurrent,
    validatingChapterIds
  } = useValidations(projectId);

  const currentlyValidatingIndex = questions.findIndex((q) => q.slug === currentlyValidating);

  const isValidating = validatingChapterIds.has(activeChapter?.id ?? 0);

  return (
    <Card data-testid="validation-panel" className="flex flex-col p-0 flex-grow relative overflow-hidden gap-0">
      <ValidationStats
        currentlyValidatingIndex={currentlyValidatingIndex}
        questions={questions}
        isCurrent={isCurrent}
        updateCurrent={updateCurrent}
        validateAll={validateAll}
        isValidating={isValidating}
        isChapterContentReady={isChapterContentReady}
      />

      {isFetchingQuestions && (
        <Card className="flex justify-center items-center gap-2 pt-0 flex-grow relative bg-innoveraZinc-100 overflow-hidden text-zinc-500 text-sm">
          <LoadingSpinner />
          <Typography variant="body3" className="text-zinc-500 text-center font-light">
            Loading analysis questions...
          </Typography>
        </Card>
      )}

      {!isFetchingQuestions && questions.length === 0 && (
        <Card className="h-full flex flex-col gap-2 items-center justify-center bg-innoveraZinc-100 rounded-b-[8px]">
          <Typography variant="body3" className="text-zinc-500 text-center font-light">
            No analysis questions.
          </Typography>
        </Card>
      )}

      {!isFetchingQuestions && questions.length > 0 && (
        <CardContent className="h-full flex flex-col gap-1 p-1 bg-innoveraZinc-100 overflow-y-auto rounded-b-[8px]">
          {questions.map((question, index) => {
            const disabled = !!isValidating && index >= currentlyValidatingIndex;

            return (
              <div
                key={question.slug}
                data-testid={`question-${question.slug}`}
                className={cn("flex flex-col gap-3 p-3 transition-all duration-100 bg-white rounded-[8px]")}
              >
                <div className="flex items-start gap-1 text-zinc-700">
                  {currentlyValidating === question.slug && <LoadingSpinner />}
                  <div className={cn("flex-1 gap-2", disabled && "opacity-70")}>
                    <Typography variant="body2" className="text-innoveraZinc-900">
                      {question.question}
                    </Typography>
                  </div>
                </div>

                <div className="flex gap-2 items-center">
                  <AppButton
                    data-testid={`question-${question.slug}-yes`}
                    appearance={question.answer === "yes" ? "contained" : "text"}
                    size="sm"
                    disabled={disabled}
                    className={cn(
                      baseButtonClasses,
                      getAnswerStyles("button", question.answer, question.answerSource, "yes")
                    )}
                    onClick={() => submitQuestion(question.slug, "yes")}
                  >
                    <CheckIcon className="h-4 w-4" />
                    <Typography variant="label3">Yes</Typography>
                  </AppButton>

                  <AppButton
                    data-testid={`question-${question.slug}-no`}
                    appearance={question.answer === "no" ? "contained" : "text"}
                    size="sm"
                    disabled={disabled}
                    className={cn(
                      baseButtonClasses,
                      getAnswerStyles("button", question.answer, question.answerSource, "no")
                    )}
                    onClick={() => submitQuestion(question.slug, "no")}
                  >
                    <XIcon className="h-4 w-4" />
                    <Typography variant="label3">No</Typography>
                  </AppButton>

                  <AppButton
                    data-testid={`question-${question.slug}-na`}
                    appearance={question.answer === "na" ? "contained" : "text"}
                    size="sm"
                    disabled={disabled}
                    className={cn(
                      baseButtonClasses,
                      getAnswerStyles("button", question.answer, question.answerSource, "na")
                    )}
                    onClick={() => submitQuestion(question.slug, "na")}
                  >
                    <ProhibitIcon className="h-4 w-4" />
                    <Typography variant="label3">N/A</Typography>
                  </AppButton>
                </div>

                {question.explanation && (
                  <Typography
                    variant="body2"
                    className={cn(
                      baseExplanationClasses,
                      getAnswerStyles("explanation", question.answer, question.answerSource, question.answer)
                    )}
                  >
                    {question.explanation}
                  </Typography>
                )}
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
}
